import { api } from "services/axios/api";
import { getErrorsByApi } from "shared/helpers/getErrorsByApi";
import { RetornoMidia, RetornoNomeMidia, RetornoRemocaoMidia } from "../../types/midia.types";
import {
  IKit,
  IKitMedida,
  IKitEspessura,
  IKitCor,
  IKitComposicao,
  ISelectOption,
} from "../types/kit.types";

// ===== CRUD =====

export const obterKitPorId = async (idKit: number): Promise<IKit | null> => {
  try {
    const { data } = await api.get(`/KitN1/ObterPorId/${idKit}`);
    
    if (!data) return null;

    // Mapear tipo do IdTipo
    const tipo = data.idTipo === 1 ? 'Ferragem' : data.idTipo === 2 ? 'Engenharia' : undefined;

    // FASE 05 — 6ª validação/PO 2026-08-11: a API devolve a composição com os nomes do DTO
    // (tipoMaterial, un, defVariaveisUsinagem); a tela usa tipoProduto/idUnidade. Sem este
    // mapeamento, ao reabrir o kit o Produto voltava "Livre", a Unidade vazia e a Usinagem some.
    // (idUnidade é resolvido no index quando a lista de unidades estiver carregada.)
    const composicao = (data.composicao || []).map((c: any) => ({
      ...c,
      tipoProduto: c.tipoProduto || c.tipoMaterial || "Livre",
      unidadeDescricao: c.unidadeDescricao ?? c.un ?? "",
      defVariaveisUsinagem: c.defVariaveisUsinagem ?? "",
    }));

    return {
      ...data,
      tipo,
      composicao,
      // FASE 05 Ajustes — PO 2026-08-22 (item 1): a API devolve a URL no campo "imagem";
      // a tela lê kit.imagemUrl — sem este mapeamento a imagem sumia ao reabrir/recarregar.
      imagemUrl: data.imagem || undefined,
      // Ajustes 25/08 (plano de mídias, Etapa B): o NOME vem da API — antes era derivado da
      // URL com SAS e a tela exibia o GUID junto da query string inteira.
      imagemNomeArquivo: data.imagemNomeArquivo || undefined,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter kit");
    return null;
  }
};

export const salvarKit = async (kit: Partial<IKit>): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const isEdicao = !!kit.idKit;
    const endpoint = isEdicao ? `/KitN1/Atualizar/${kit.idKit}` : '/KitN1/Criar';
    const method = isEdicao ? 'put' : 'post';
    
    const dadosParaEnviar: any = { ...kit };
    
    delete dadosParaEnviar.imagemUrl;
    delete dadosParaEnviar.imagemNomeArquivo;
    delete dadosParaEnviar.tipo;

    // Mapear tipo para IdTipo
    if (kit.tipo) {
      dadosParaEnviar.idTipo = kit.tipo === 'Ferragem' ? 1 : kit.tipo === 'Engenharia' ? 2 : null;
    }

    const { data } = await api[method](endpoint, dadosParaEnviar);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao salvar kit");
    return null;
  }
};

export const excluirKit = async (idKit: number): Promise<boolean> => {
  try {
    const { data } = await api.delete(`/KitN1/Excluir/${idKit}`);
    return data?.sucesso || false;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao excluir kit");
    return false;
  }
};

export const duplicarKit = async (idKit: number): Promise<{ id: number; sucesso: boolean; mensagem: string } | null> => {
  try {
    const { data } = await api.post(`/KitN1/Duplicar/${idKit}`);
    return data;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao duplicar kit");
    return null;
  }
};

export const validarCodigoUnico = async (codigo: string, idExcluir?: number): Promise<boolean> => {
  try {
    const params = new URLSearchParams({ codigo });
    if (idExcluir) {
      params.append('idExcluir', idExcluir.toString());
    }
    const { data } = await api.get(`/KitN1/ValidarCodigoUnico?${params.toString()}`);
    return data?.valido || false;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao validar código");
    return false;
  }
};

// ===== Listas de Referência =====

export const listarReferencias = async (idTipoTela?: number): Promise<ISelectOption[]> => {
  try {
    const params: any = {};
    if (idTipoTela) params.idTipoTela = idTipoTela;
    const { data } = await api.get("/ReferenciaDados/Referencias", { params });
    if (!data || !Array.isArray(data)) {
      console.warn("listarReferencias: dados inválidos ou não é array", data);
      return [];
    }
    const mapped = data.map((item: any) => ({
      id: item.idReferencia || item.IdReferencia || item.id || item.Id,
      nome: item.descricao || item.Descricao || item.nome || item.Nome || "",
      descricao: item.descricao || item.Descricao || "",
    }));
    console.log("listarReferencias: mapeados", mapped.length, "itens");
    return mapped;
  } catch (error: any) {
    console.error("Erro ao listar referências:", error);
    getErrorsByApi(error, "Erro ao listar referências");
    return [];
  }
};

export const listarLinhas = async (idReferencia?: number): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — S3/PO 2026-05-13: quando idReferencia informado, filtra via tabela ReferenciaLinhas.
    const params: any = {};
    if (idReferencia) params.idReferencia = idReferencia;
    const { data } = await api.get("/ReferenciaDados/Linhas", { params });
    if (!data || !Array.isArray(data)) {
      console.warn("listarLinhas: dados inválidos ou não é array", data);
      return [];
    }
    const mapped = data.map((item: any) => ({
      id: item.idLinha || item.IdLinha || item.id || item.Id,
      nome: item.descricao || item.Descricao || item.nome || item.Nome || "",
      descricao: item.descricao || item.Descricao || "",
    }));
    return mapped;
  } catch (error: any) {
    console.error("Erro ao listar linhas:", error);
    getErrorsByApi(error, "Erro ao listar linhas");
    return [];
  }
};

export const listarGrupos = async (): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: kits usam as flags de Componente no
    // Cadastro de Grupos — o backend filtra quando tipoMaterial=kits é informado.
    const { data } = await api.get("/ReferenciaDados/Grupos", { params: { tipoMaterial: "kits" } });
    if (!data || !Array.isArray(data)) {
      console.warn("listarGrupos: dados inválidos ou não é array", data);
      return [];
    }
    const mapped = data.map((item: any) => ({
      id: item.idGrupo || item.IdGrupo || item.idGrupoMaterial || item.IdGrupoMaterial || item.id || item.Id,
      nome: item.descricao || item.Descricao || item.nome || item.Nome || "",
      descricao: item.descricao || item.Descricao || "",
    }));
    console.log("listarGrupos: mapeados", mapped.length, "itens");
    return mapped;
  } catch (error: any) {
    console.error("Erro ao listar grupos:", error);
    getErrorsByApi(error, "Erro ao listar grupos");
    return [];
  }
};

export const listarTiposKit = async (): Promise<ISelectOption[]> => {
  try {
    // Mock por enquanto - será implementado no backend
    return [
      { id: 1, nome: 'Ferragem', descricao: 'Ferragem' },
      { id: 2, nome: 'Engenharia', descricao: 'Engenharia' },
    ];
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao listar tipos de kit");
    return [];
  }
};

export const listarCoresKit = async (): Promise<ISelectOption[]> => {
  try {
    // FASE 05 — 4ª validação/Frente 9 2026-07-30: kits usam as flags de Componente no
    // cadastro de cores — o backend filtra quando tipoMaterial=kits é informado.
    const { data } = await api.get("/ReferenciaDados/Cores", { params: { tipoMaterial: "kits" } });
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: any) => ({
      id: item.id || item.Id || item.idCorMaterial || item.IdCorMaterial,
      nome: item.descricao || item.Descricao || item.nome || item.Nome || "",
      descricao: item.descricao || item.Descricao || "",
    }));
  } catch (error: any) {
    console.warn("Erro ao listar cores:", error);
    return [];
  }
};

export const listarUnidades = async (): Promise<ISelectOption[]> => {
  try {
    const { data } = await api.get("/ReferenciaDados/Unidades");
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: any) => ({
      id: item.id || item.Id || item.idUnidadeItemEmbalagem || item.IdUnidadeItemEmbalagem,
      nome: item.descricao || item.Descricao || item.nome || item.Nome || "",
      descricao: item.descricao || item.Descricao || "",
      // FASE 05 — 6ª validação/PO 2026-08-11: sigla (PC, BR…) — gravada em KitComposicao.UN.
      sigla: item.sigla || item.Sigla || "",
    }));
  } catch (error: any) {
    console.warn("Erro ao listar unidades:", error);
    return [];
  }
};

// ===== Listas para Composição =====

export const listarPerfisAtivos = async (): Promise<ISelectOption[]> => {
  try {
    // Usar validateStatus para não lançar erro em 404 (endpoint pode não existir ainda)
    const response = await api.get("/PerfilN1/ListarAtivos", {
      validateStatus: (status) => status < 500, // Aceita 404 sem lançar erro
    });
    
    // Se for 404, retorna array vazio silenciosamente
    if (response.status === 404) {
      return [];
    }
    
    const data = response.data;
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: any) => ({
      id: item.idPerfil || item.IdPerfil || item.id || item.Id,
      nome: item.descricao || item.Descricao || item.codigo || item.Codigo || item.nome || item.Nome || "",
      descricao: item.descricao || item.Descricao || "",
    }));
  } catch (error: any) {
    // Silenciosamente retorna array vazio - endpoint pode não existir ainda
    return [];
  }
};

export const listarComponentesAtivos = async (): Promise<ISelectOption[]> => {
  try {
    // Usar validateStatus para não lançar erro em 404 (endpoint pode não existir ainda)
    const response = await api.get("/ComponenteN1/ListarAtivos", {
      validateStatus: (status) => status < 500, // Aceita 404 sem lançar erro
    });
    
    // Se for 404, retorna array vazio silenciosamente
    if (response.status === 404) {
      return [];
    }
    
    const data = response.data;
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: any) => ({
      id: item.idComponente || item.IdComponente || item.id || item.Id,
      nome: item.descricao || item.Descricao || item.codigo || item.Codigo || item.nome || item.Nome || "",
      descricao: item.descricao || item.Descricao || "",
    }));
  } catch (error: any) {
    // Silenciosamente retorna array vazio - endpoint pode não existir ainda
    return [];
  }
};

// FASE 05 — 6ª validação/PO 2026-08-11: busca por termo para a Composição do Kit (autocomplete),
// replicando Perfis > Composição. As funções ListarAtivos acima batiam em endpoints inexistentes
// (404 → lista vazia) e por isso o campo Código não apresentava nada.
export interface IOpcaoComposicao {
  id: number;
  codigo: string;
  descricao: string;
}

export const buscarPerfisComposicao = async (termo: string): Promise<IOpcaoComposicao[]> => {
  try {
    const { data } = await api.get("/PerfilN1/BuscarPorCodigo", { params: { termo } });
    return (data?.items || []).map((item: any) => ({
      id: item.idPerfil,
      codigo: item.codigo,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao buscar perfis para composição:", error);
    return [];
  }
};

export const buscarComponentesComposicao = async (termo: string): Promise<IOpcaoComposicao[]> => {
  try {
    const { data } = await api.get("/ComponenteN1/BuscarPorCodigo", { params: { termo } });
    return (data?.items || []).map((item: any) => ({
      id: item.idComponente,
      codigo: item.codigo,
      descricao: item.descricao,
    }));
  } catch (error: any) {
    console.error("Erro ao buscar componentes para composição:", error);
    return [];
  }
};

export const obterPerfilPorId = async (idPerfil: number): Promise<ISelectOption | null> => {
  try {
    const { data } = await api.get(`/PerfilN1/ObterPorId/${idPerfil}`);
    if (!data) return null;
    return {
      id: data.idPerfil,
      nome: data.codigo,
      descricao: data.descricao,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter perfil");
    return null;
  }
};

export const obterComponentePorId = async (idComponente: number): Promise<ISelectOption | null> => {
  try {
    const { data } = await api.get(`/ComponenteN1/ObterPorId/${idComponente}`);
    if (!data) return null;
    return {
      id: data.idComponente,
      nome: data.codigo,
      descricao: data.descricao,
    };
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter componente");
    return null;
  }
};

// ===== Gerenciamento de Mídias =====

// Plano 21/09/2026 (Frente 3): as escritas de mídia devolvem também o `id` do registro gravado —
// com o produto Disponível, a API grava na cópia de trabalho e a tela precisa seguir para ela.
export const uploadImagem = async (idKit: number, file: File, nomeArquivo?: string): Promise<RetornoMidia | null> => {
  try {
    const formData = new FormData();
    formData.append('arquivo', file);
    // Ajustes 25/08 (plano de mídias, Etapa A): o servidor grava com este nome — sem ele,
    // o arquivo era renomeado para um GUID e perdia o vínculo com os produtos.
    if (nomeArquivo) formData.append('nomeArquivo', nomeArquivo);
    const { data } = await api.post(`/KitN1/${idKit}/Midias/UploadImagem`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data?.url ? { url: data.url, id: data.id ?? idKit } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao fazer upload da imagem");
    return null;
  }
};

export const removerImagem = async (idKit: number): Promise<RetornoRemocaoMidia | null> => {
  try {
    const { data } = await api.delete(`/KitN1/${idKit}/Midias/RemoverImagem`);
    return data?.sucesso ? { sucesso: true, id: data.id ?? idKit } : null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao remover imagem");
    return null;
  }
};

// ===== Histórico =====

export const obterHistoricoKit = async (idKit: number): Promise<any[] | null> => {
  try {
    const { data } = await api.get(`/KitN1/${idKit}/Historico`);
    return data?.items || [];
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao obter histórico de atualizações");
    return null;
  }
};

// ===== Definir apenas o NOME do arquivo (sem upload) =====

/**
 * Reporte de 31/08 (item 6): grava só o nome informado, sem enviar arquivo — é assim que o
 * produto assume uma mídia que já foi carregada por outro produto da empresa (F05A-P3b).
 * Devolve `arquivoExiste` para a tela avisar quando o nome ainda não tem arquivo no acervo.
 */
export const definirNomeMidiaKit = async (
  idKit: number,
  nomeArquivo: string
): Promise<RetornoNomeMidia | null> => {
  try {
    const { data } = await api.put(`/KitN1/${idKit}/Midias/DefinirNome`, {
      tipoMidia: "figura",
      nomeArquivo,
    });
    return data ?? null;
  } catch (error: any) {
    getErrorsByApi(error, "Erro ao gravar o nome do arquivo");
    return null;
  }
};
